import React from "react";
import {doppeActionTypeDefineViewerMetadata} from "../../client-server-common/doppe-action-types/doppe-action-type";
import {doppeActionTypeLink} from "./doppe-action-type-link";
import {
    DoppeActionCTATypeLinkOpenType,
    doppeLinkSpecIsEntity,
    doppeLinkSpecIsPage,
    doppeLinkSpecIsUrl,
    DoppeLinkSpecEntityType
} from "../../client-server-common/types/doppe-action-cta-button";
import {
    bymoPageGetInnerNavigatablePageById,
    DoppeDtoInnerPageType
} from "../../client-server-common/types/doppe-dto-inner-page";
import {DoppeDtoStoreProductType} from "../../client-server-common/types/doppe-dto-store-product";
import {DoppeDtoBookingServiceType} from "../../client-server-common/types/doppe-dto-booking-service";
import {DoppeDtoEventRegistrationType} from "../../client-server-common/types/doppe-dto-event";
import {DoppeViewerModalPanelLayout} from "../../components/doppe-viewer-modal-panel/doppe-viewer-modal-panel";
import {
    doppeViewerActionContextOpenModal,
    doppeViewerActionContextOpenUrlInIframeModal
} from "../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context-utils";
import {
    DoppeActionModalIframeContent
} from "../../components/doppe-action-modal-iframe-content/doppe-action-modal-iframe-content";
import type {IDoppeViewerActionContext} from "../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context";
import {bymoPageContextPropsResolveEntityInfo} from "../../doppe-sdk/bymo-page/bymo-page-context-props";
import {BymoPageViewerMode} from "../../components/bymo-page-viewer-context/bymo-page-viewer-context";
import {
    viewerEntityNavigationResolveProductTarget,
    viewerEntityNavigationResolveServiceTarget,
    viewerEntityNavigationResolveBlogPostTarget,
    viewerEntityNavigationResolveEventTarget,
    viewerListNavigationResolveProductsListTarget,
    viewerListNavigationResolveServicesListTarget,
    viewerListNavigationResolveBlogPostsListTarget,
    viewerListNavigationResolveEventsListTarget
} from "../../client-server-common";
import {DoppeViewerLang} from "../../client-server-common/doppe-viewer/doppe-viewer-lang";

/**
 * Helper function to navigate to a page using the router.
 * For vertical list pages, checks the useHoppVertical*ListPage settings.
 * In LivePreview mode, external URLs are opened in a modal.
 * In Live mode, external URLs navigate in the same tab.
 */
function navigateToPageViaRouter(
    pageId: string,
    actionContext: IDoppeViewerActionContext<any>
) {
    actionContext.performContextCallback(async ({clientServices, viewerContextData, bymoPageProps}) => {
        const router = clientServices.viewerRouterClientService;
        const page = bymoPageGetInnerNavigatablePageById(pageId, bymoPageProps);
        const bymoPageContextProps = actionContext.getBymoPageContextProps();
        const isLivePreviewMode = viewerContextData.mode === BymoPageViewerMode.LivePreview;
        const showMobileView = true;
        
        // Create navigation options for list pages
        const listNavigationOptions = {
            bymoPageContextProps: bymoPageContextProps,
            viewerLang: DoppeViewerLang.English,
            showMobileView: showMobileView,
            isLivePreviewMode: isLivePreviewMode
        };
        
        // Handle external URL - open in modal for LivePreview, navigate for Live
        const handleExternalUrl = (externalUrl: string) => {
            if (isLivePreviewMode) {
                doppeViewerActionContextOpenUrlInIframeModal(actionContext, externalUrl);
            } else {
                window.location.href = externalUrl;
            }
        };
        
        if (page) {
            switch (page.type) {
                case DoppeDtoInnerPageType.Products: {
                    const result = viewerListNavigationResolveProductsListTarget(listNavigationOptions);
                    if (result.useInternalRouting) {
                        router.navigateToProductsList();
                    } else if (result.externalUrl) {
                        handleExternalUrl(result.externalUrl);
                    }
                    break;
                }
                case DoppeDtoInnerPageType.BlogPosts: {
                    const result = viewerListNavigationResolveBlogPostsListTarget(listNavigationOptions);
                    if (result.useInternalRouting) {
                        router.navigateToBlogPostsList();
                    } else if (result.externalUrl) {
                        handleExternalUrl(result.externalUrl);
                    }
                    break;
                }
                case DoppeDtoInnerPageType.Events: {
                    const result = viewerListNavigationResolveEventsListTarget(listNavigationOptions);
                    if (result.useInternalRouting) {
                        router.navigateToEventsList();
                    } else if (result.externalUrl) {
                        handleExternalUrl(result.externalUrl);
                    }
                    break;
                }
                case DoppeDtoInnerPageType.Services: {
                    const result = viewerListNavigationResolveServicesListTarget(listNavigationOptions);
                    if (result.useInternalRouting) {
                        router.navigateToServicesList();
                    } else if (result.externalUrl) {
                        handleExternalUrl(result.externalUrl);
                    }
                    break;
                }
                case DoppeDtoInnerPageType.Cart: {
                    router.navigateToCart();
                    break;
                }
                case DoppeDtoInnerPageType.Content:
                default:
                    router.navigateToContentPage(page.slug);
                    break;
            }
        }
    });
}

/**
 * Helper function to navigate to an entity item page using the router.
 * Uses useHoppVerticalItemPages flags to determine internal vs external navigation.
 * In LivePreview mode, external URLs are opened in a modal.
 * In Live mode, external URLs navigate in the same tab.
 */
function navigateToEntityViaRouter(
    entityType: DoppeLinkSpecEntityType,
    entityId: string,
    actionContext: IDoppeViewerActionContext<any>
) {
    actionContext.performContextCallback(async ({clientServices, viewerContextData}) => {
        const router = clientServices.viewerRouterClientService;
        const bymoPageContextProps = actionContext.getBymoPageContextProps();
        const isLivePreviewMode = viewerContextData.mode === BymoPageViewerMode.LivePreview;

        // Resolve entity info from business entities data (includes slug and pageUrl)
        const entityInfo = bymoPageContextPropsResolveEntityInfo(
            bymoPageContextProps,
            entityType,
            entityId
        );
        const slug = entityInfo.slug;
        
        // Create navigation options
        const navigationOptions = {
            bymoPageContextProps: bymoPageContextProps,
            viewerLang: DoppeViewerLang.English,
            showMobileView: true,
            isLivePreviewMode: isLivePreviewMode
        };
        
        // Handle external URL - open in modal for LivePreview, navigate for Live
        const handleExternalUrl = (externalUrl: string) => {
            if (isLivePreviewMode) {
                doppeViewerActionContextOpenUrlInIframeModal(actionContext, externalUrl);
            } else {
                window.location.href = externalUrl;
            }
        };
        
        switch (entityType) {
            case DoppeLinkSpecEntityType.Product: {
                const result = viewerEntityNavigationResolveProductTarget(
                    {slug: slug, productPageUrl: entityInfo.pageUrl},
                    navigationOptions
                );
                if (result.useInternalRouting && result.slug) {
                    // Route to the appropriate page based on product type
                    if (entityInfo.innerType === DoppeDtoStoreProductType.Digital) {
                        router.navigateToDigitalProductItem(result.slug);
                    } else {
                        // Default to physical product for physical/unspecified types
                        router.navigateToProductItem(result.slug);
                    }
                } else if (result.externalUrl) {
                    handleExternalUrl(result.externalUrl);
                }
                break;
            }
            case DoppeLinkSpecEntityType.BlogPost: {
                const result = viewerEntityNavigationResolveBlogPostTarget(
                    {slug: slug, pageUrl: entityInfo.pageUrl},
                    navigationOptions
                );
                if (result.useInternalRouting && result.slug) {
                    router.navigateToBlogPostItem(result.slug);
                } else if (result.externalUrl) {
                    handleExternalUrl(result.externalUrl);
                }
                break;
            }
            case DoppeLinkSpecEntityType.Event: {
                const result = viewerEntityNavigationResolveEventTarget(
                    {slug: slug, pageUrl: entityInfo.pageUrl},
                    navigationOptions
                );
                if (result.useInternalRouting && result.slug) {
                    // Route to the appropriate page based on event registration type
                    if (entityInfo.innerType === DoppeDtoEventRegistrationType.Ticketing) {
                        router.navigateToTicketingEventItem(result.slug);
                    } else {
                        // Default to RSVP event for RSVP/Unknown types
                        router.navigateToEventItem(result.slug);
                    }
                } else if (result.externalUrl) {
                    handleExternalUrl(result.externalUrl);
                }
                break;
            }
            case DoppeLinkSpecEntityType.Service: {
                const result = viewerEntityNavigationResolveServiceTarget(
                    {slug: slug, servicePageUrl: entityInfo.pageUrl},
                    navigationOptions
                );
                if (result.useInternalRouting && result.slug) {
                    // Route to the appropriate page based on service type
                    switch (entityInfo.innerType) {
                        case DoppeDtoBookingServiceType.COURSE:
                            router.navigateToCourseItem(result.slug);
                            break;
                        case DoppeDtoBookingServiceType.Class:
                            router.navigateToClassItem(result.slug);
                            break;
                        case DoppeDtoBookingServiceType.APPOINTMENT:
                        default:
                            // Default to appointment for unknown/unspecified types
                            router.navigateToAppointmentItem(result.slug);
                            break;
                    }
                } else if (result.externalUrl) {
                    handleExternalUrl(result.externalUrl);
                }
                break;
            }
        }
    });
}

export const doppeActionTypeLinkViewer = doppeActionTypeDefineViewerMetadata(doppeActionTypeLink, {
    getActionButtonProps: actionContext => {

        const action = actionContext.action;
        const url = action.settings.url;
        const openType = action.settings.openType;
        
        // For Page link specs, always navigate via router (same tab)
        // openType is ignored for non-URL link specs
        if (doppeLinkSpecIsPage(url)) {
            const pageId = url.pageId;
            if (pageId) {
                return {
                    onClick: () => {
                        navigateToPageViaRouter(pageId, actionContext);
                    }
                };
            }
        }
        
        // For Entity link specs (catalog items), navigate to entity item page via router
        if (doppeLinkSpecIsEntity(url)) {
            const entityType = url.entityType;
            const entityId = url.entityId;
            if (entityType && entityId) {
                return {
                    onClick: () => {
                        navigateToEntityViaRouter(entityType, entityId, actionContext);
                    }
                };
            }
        }
        
        // For URL links only: handle openType
        if (doppeLinkSpecIsUrl(url)) {
            // For InPage (popup) mode, show content in modal
            if (openType === DoppeActionCTATypeLinkOpenType.InPage) {
                return {
                    onClick: () => {
                        // Use the same modal pattern as CTA buttons - with header and close button
                        doppeViewerActionContextOpenModal(
                            actionContext,
                            () => ({
                                render: () => (
                                    <DoppeActionModalIframeContent
                                        withMinHeight={false}
                                        src={url}
                                    />
                                ),
                                dependencies: []
                            }),
                            {
                                mobileLayout: DoppeViewerModalPanelLayout.InnerFull,
                                desktopLayout: DoppeViewerModalPanelLayout.InnerFull,
                                showHeader: true,
                                showHeaderAsCloseButton: false,
                                title: action.name
                            }
                        );
                    }
                };
            }
            
            // For URL with NewTab or SameTab, use standard href approach
            const target = openType === DoppeActionCTATypeLinkOpenType.NewTab ? '_blank' : '_self';
            
            return {
                href: url,
                target: target,
                rel: openType === DoppeActionCTATypeLinkOpenType.NewTab ? 'noopener' : undefined,
            };
        }

        // Fallback: no url set
        return {};
    },
    
    activateCTA: actionContext => {
        const action = actionContext.action;
        const url = action.settings.url;
        const openType = action.settings.openType;
        
        // For Page link specs, always navigate via router (same tab)
        // openType is ignored for non-URL link specs
        if (doppeLinkSpecIsPage(url)) {
            const pageId = url.pageId;
            if (pageId) {
                navigateToPageViaRouter(pageId, actionContext);
            }
            return;
        }
        
        // For Entity link specs (catalog items), navigate to entity item page via router
        if (doppeLinkSpecIsEntity(url)) {
            const entityType = url.entityType;
            const entityId = url.entityId;
            if (entityType && entityId) {
                navigateToEntityViaRouter(entityType, entityId, actionContext);
            }
            return;
        }
        
        // For URL links only: handle openType
        if (doppeLinkSpecIsUrl(url)) {
            // For InPage (popup) mode, show content in modal with header and close button
            if (openType === DoppeActionCTATypeLinkOpenType.InPage) {
                doppeViewerActionContextOpenModal(
                    actionContext,
                    () => ({
                        render: () => (
                            <DoppeActionModalIframeContent
                                withMinHeight={false}
                                src={url}
                            />
                        ),
                        dependencies: []
                    }),
                    {
                        mobileLayout: DoppeViewerModalPanelLayout.InnerFull,
                        desktopLayout: DoppeViewerModalPanelLayout.InnerFull,
                        showHeader: true,
                        showHeaderAsCloseButton: false,
                        title: action.name
                    }
                );
                return;
            }
            
            // For SameTab or NewTab URL links
            const newTab = openType === DoppeActionCTATypeLinkOpenType.NewTab;
            actionContext.navigateToUrl(url, newTab);
        }
    }
})